import Cookies from "universal-cookie";
import { AlertModalBinIcon } from "../../../assets";
import { BaseAlertModal } from "../../../components/modal/alert";
import { AppLayout } from "../../../containers/layout/app";
import { GLGroupManagementWrapper } from "./styled";
import { useNavigate } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { AppContext } from "../../../context/appContext";
import { Box, Grid2, Stack, Typography } from "@mui/material";
import { GLGroupManagementTable } from "../../../containers/tables/glgroupmanagementtable";
import { useQuery } from "@tanstack/react-query";
import { deleteChartOfAccountService } from "../../../utils/api/accounting/chartofaccount/deleteChartOfAccount";
import { retrieveAllGLGroupService } from "../../../utils/api/accounting/chartofaccount/retrieveAllGlGroup";
import { BaseButton } from "../../../components/button/styled";

export const GlGroupManagement = () => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const navigate = useNavigate();
	const { setActiveNavItem } = useContext(AppContext);

	const [error, setError] = useState<string | null>(null);
	const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
	const [chartOfAccountIdToDelete, setChartOfAccountIdToDelete] = useState<
		string | null
	>(null);
	const [isFetching, setIsFetching] = useState(false);
	const [paginationIndex, setPaginationIndex] = useState({
		page: "1",
		perPage: "10",
		totalPages: "1",
	});

	useEffect(() => {
		setActiveNavItem("Configuration");
	}, [setActiveNavItem]);

	const { data: chartsOfAccount, refetch: refetchChartsOfAccount } = useQuery({
		queryKey: ["glGroupManagement", TOKEN, paginationIndex],
		queryFn: async () => {
			setIsFetching(true);
			const response = await retrieveAllGLGroupService(TOKEN, paginationIndex);
			const transformedResponse = response?.data;
			transformedResponse.sort(
				(a: Record<string, any>, b: Record<string, any>) => {
					return Number(a.glCode) - Number(b.glCode);
				},
			);
			if (!Array.isArray(response) && response?.meta) {
				setPaginationIndex((prev) => ({
					...prev,
					totalPages: String(response?.meta?.totalPages ?? "1"),
				}));
			}
			setIsFetching(false);
			return transformedResponse;
		},
		enabled: !!TOKEN,
	});

	const handleAlertModalPersist = () => {
		return setIsAlertModalOpen(true);
	};

	const handleAlertModalCallToActionClick = () => {
		return setIsAlertModalOpen(false);
	};

	const handleDeleteChartOfAccount = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		institutionId: string,
		chartOfAccountId: string,
	) => {
		e.preventDefault();
		setError(null);
		setChartOfAccountIdToDelete(chartOfAccountId);
		try {
			const response = await deleteChartOfAccountService(
				TOKEN,
				institutionId,
				chartOfAccountId,
			);
			if (response.status === "success") {
				setChartOfAccountIdToDelete(null);
				setIsAlertModalOpen(true);
				refetchChartsOfAccount();
			} else {
				setChartOfAccountIdToDelete(null);
				setError("Chart of account deletion failed. Please try again.");
			}
		} catch (error: any) {
			setChartOfAccountIdToDelete(null);
			setError(`Chart of account deletion failed. ${error.message}`);
			console.error("Chart of account deletion failed:", error);
		}
	};

	const handleActionClick = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		id: string | Record<any, any>,
		action?: string,
	) => {
		e.preventDefault();
		switch (action) {
			case "edit":
				return navigate(`/accounting/chartofaccount/${id}`);
			case "delete":
				if (typeof id !== "string") {
					const { institutionId, glGroupId } = id;
					return handleDeleteChartOfAccount(e, institutionId, glGroupId);
				}
				break;
			default:
				console.warn(`Unhandled action: ${action}`);
		}
	};

	const handlePagination = (
		e: React.MouseEvent<HTMLButtonElement>,
		type: "previous" | "next",
	) => {
		e.preventDefault();
		setPaginationIndex((prev) => {
			const currentPage = parseInt(prev.page, 10);
			const newPage =
				type === "previous"
					? Math.max(1, currentPage - 1)
					: Math.min(parseInt(prev.totalPages, 10), currentPage + 1);
			return {
				...prev,
				page: newPage.toString(),
			};
		});
	};

	return (
		<AppLayout pageId={"GL-Group-Management"} pageTitle={"GL Group Management"}>
			<GLGroupManagementWrapper>
				{/* alert modal below */}
				<BaseAlertModal
					open={isAlertModalOpen}
					icon={<AlertModalBinIcon />}
					title="GL Group Successfully Deleted"
					callToAction="Close"
					handleClose={handleAlertModalPersist}
					handleCallToAction={handleAlertModalCallToActionClick}
				/>
				{/* alert modal above */}
				<Box component={"div"} className="gl-group-management-table-box">
					<GLGroupManagementTable
						rows={chartsOfAccount as Record<any, any>[]}
						selectedGLGroupId={chartOfAccountIdToDelete}
						handleItemClick={handleActionClick}
					/>
				</Box>
				<Grid2 container component={"div"} spacing={"var(--flex-gap)"}>
					<Grid2
						size={{ mobile: 12, laptop: 8 }}
						display={{ mobile: "none", laptop: "grid" }}
					></Grid2>
					<Grid2 size={{ mobile: 12, laptop: 4 }}>
						<Stack
							direction={"row"}
							overflow={"hidden"}
							borderRadius={"6px"}
							alignItems={"center"}
							justifyContent={"space-around"}
							border={"1px solid var(--input-field-border-color)"}
						>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									disabled={
										parseInt(paginationIndex.page, 10) === 1 || isFetching
									}
									onClick={(e) => handlePagination(e, "previous")}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Previous
									</Typography>
								</BaseButton>
							</Box>
							<Box
								display={"flex"}
								overflow={"hidden"}
								bgcolor={"var(--primary-color)"}
								padding={"calc(var(--basic-padding)/2)"}
							>
								<Typography
									variant="h3"
									fontFamily={"Roboto"}
									fontWeight={600}
									fontSize={10}
									lineHeight={"normal"}
									color="var(--light-color)"
								>
									{`${paginationIndex.page} / ${paginationIndex.totalPages}`}
								</Typography>
							</Box>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									onClick={(e) => handlePagination(e, "next")}
									disabled={
										paginationIndex.page === paginationIndex.totalPages ||
										isFetching
									}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Next
									</Typography>
								</BaseButton>
							</Box>
						</Stack>
					</Grid2>
				</Grid2>
				{error && (
					<Typography
						fontFamily={"Inter"}
						fontWeight={"600"}
						fontSize={14}
						lineHeight={"normal"}
						color={"var(--text-salmon-pink-color)"}
						whiteSpace={"normal"}
					>
						{error}
					</Typography>
				)}
			</GLGroupManagementWrapper>
		</AppLayout>
	);
};
